import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';
import AskClaimRefNumberDialog from './AskClaimRefNumberDialog';
import EdiTextDialog from './EdiTextDialog';

const propTypes = {
  title: PropTypes.string,
  invoices: PropTypes.array,
};

const defaultProps = {
  title: '',
  invoices: [],
};

@autobind
class TableEbillingInvoices extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      exportingSelectedInvoices: false,

      ediText: '',
      isEdiTextDialogVisible: false,
      isAskClaimRefNumberDialogVisible: false,
    };
  }

  async onPrintSelectedInvoicesClick() {
    const selectedInvoiceIds = this.dataTables.getSelectedRowKeys();
    if (selectedInvoiceIds.length > 0) {
      try {
        this.setState({
          exportingSelectedInvoices: true,
        });

        const params = { ids: selectedInvoiceIds };
        const ediText = await this.callServiceForString('/invoice/export_edi', params);

        this.openEdiTextDialog(ediText);
      } finally {
        this.setState({
          exportingSelectedInvoices: false,
        });
      }
    } else {
      this.notifyWarning('No invoice is selected.');
    }
  }

  onPrintCorrectedInvoiceClick() {
    const selectedInvoiceIds = this.dataTables.getSelectedRowKeys();

    if (selectedInvoiceIds.length <= 0) {
      this.notifyWarning('No invoice is selected.');
      return;
    } else if (selectedInvoiceIds.length !== 1) {
      this.notifyWarning('You can select only one invoice for correction.');
      return;
    }

    // Show the AskClaimRefNumberDialog.
    this.setState({
      isAskClaimRefNumberDialogVisible: true,
    });
  }

  async onAskClaimRefNumberConfirm(claimRefNumber) {
    if (!claimRefNumber) {
      this.notifyWarning('Claim Reference Number cannot be empty.');
      return;
    }

    try {
      this.setState({
        exportingSelectedInvoices: true,
      });

      const selectedInvoiceIds = this.dataTables.getSelectedRowKeys();
      const params = { ids: selectedInvoiceIds, claimRefNumber };
      const ediText = await this.callServiceForString('/invoice/export_edi', params);

      this.openEdiTextDialog(ediText);
    } finally {
      this.setState({
        exportingSelectedInvoices: false,
      });
    }
  }

  openEdiTextDialog(ediText) {
    this.setState({
      ediText,
      isEdiTextDialogVisible: true,
    });
  }

  closeEdiTextDialog() {
    this.setState({
      ediText: '',
      isEdiTextDialogVisible: false,
    });
  }

  createColumns() {
    return [{
      title: 'Invoice Date',
      dataIndex: 'invoiceDateUsString',
      sorter: (a, b) => a.invoiceDate - b.invoiceDate,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.id}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Auth. Code',
      dataIndex: 'authorizationCode',
      sorter: (a, b) => a.authorizationCode?.localeCompare(b.authorizationCode),
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Visits From',
      dataIndex: 'serviceStartDateUsString',
      sorter: (a, b) => a.serviceStartDate - b.serviceStartDate,
    }, {
      title: 'Visits To',
      dataIndex: 'serviceEndDateUsString',
      sorter: (a, b) => a.serviceEndDate - b.serviceEndDate,
    }, {
      title: 'Mail Date',
      dataIndex: 'mailDateUsString',
      sorter: (a, b) => a.mailDate - b.mailDate,
    }, {
      title: 'Total Visits',
      dataIndex: 'totalVisits',
      sorter: (a, b) => a.totalVisits - b.totalVisits,
      exportAsNumber: true,
    }, {
      title: 'Total Hours',
      dataIndex: 'totalHoursString',
      sorter: (a, b) => a.totalHours - b.totalHours,
    }, {
      title: 'Total Amt.',
      dataIndex: 'amountInDollars',
      sorter: (a, b) => a.amount - b.amount,
    }, {
      title: 'Last Export',
      dataIndex: 'lastExportTimeUsString',
      sorter: (a, b) => a.lastExportTime - b.lastExportTime,
    }];
  }

  createDataSource(invoices) {
    const dataSource = [];
    invoices.forEach((invoice, index) => {
      dataSource.push({
        key: invoice.getId(),
        id: invoice.getId(),
        invoiceDate: invoice.getLong('invoiceDate'),
        invoiceDateUsString: invoice.getString('invoiceDateUsString'),
        invoiceNumber: invoice.getString('invoiceNumber'),
        authorizationCode: invoice.getString('authorizationCode'),
        companyName: invoice.getString('companyName'),
        patientId: invoice.getLong('patientId'),
        patientName: invoice.getString('patientName'),
        serviceStartDate: invoice.getLong('serviceStartDate'),
        serviceStartDateUsString: invoice.getString('serviceStartDateUsString'),
        serviceEndDate: invoice.getLong('serviceEndDate'),
        serviceEndDateUsString: invoice.getString('serviceEndDateUsString'),
        mailDate: invoice.getLong('mailDate'),
        mailDateUsString: invoice.getString('mailDateUsString'),
        totalVisits: invoice.getLong('totalVisits'),
        totalHours: invoice.getLong('totalHours'),
        totalHoursString: invoice.getString('totalHoursString'),
        amount: invoice.getDouble('amount'),
        amountInDollars: helpers.toDollars(invoice.getDouble('amount')),
        lastExportTime: invoice.getLong('lastExportTime'),
        lastExportTimeUsString: invoice.getString('lastExportTimeUsString'),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable
        columnKeysToAlign={['totalVisits', 'totalHoursString', 'amountInDollars']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('totalVisits'),
          TfootSummary.SUM_REDUCER_GENERATOR('totalHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('amount')]
        }
        formatters={[
          n => n,
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.toDollars(n),
        ]}
      />
    );
  }

  renderFooter() {
    const { exportingSelectedInvoices } = this.state;

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        padding="20px 20px"
        justifyContent="center"
        backgroundColor="containerGrayBackground"
      >
        <CustomButton
          width="wrap_content"
          height="32px"
          margin="0 16px 0 0"
          backgroundColor="yellow_gold"
          fontSize="14px"
          type="primary"
          loading={exportingSelectedInvoices}
          onClick={this.onPrintSelectedInvoicesClick}
        >
          EXPORT SELECTED INVOICES (ORIGINAL)
        </CustomButton>

        <CustomButton
          width="wrap_content"
          height="32px"
          backgroundColor="green_steel"
          fontSize="14px"
          type="primary"
          loading={exportingSelectedInvoices}
          onClick={this.onPrintCorrectedInvoiceClick}
        >
          EXPORT SELECTED INVOICES (CORRECTION)
        </CustomButton>
      </FlexRowContainer>
    );
  }

  renderContent() {
    const { invoices, title, ...layoutProps } = this.props;
    const { ediText, isEdiTextDialogVisible, isAskClaimRefNumberDialogVisible } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
      >
        <DataTables
          {...layoutProps}
          ref={(ref) => { this.dataTables = ref; }}
          color="yellow_gold"
          fontSize="12px"
          size="small"
          title={title}
          icon="file-text"
          selectable
          columns={this.createColumns()}
          dataSource={this.createDataSource(invoices)}
          tfoot={this.renderTfoot}
          footer={this.renderFooter()}
        />

        <AskClaimRefNumberDialog
          width="500px"
          height="wrap_content"
          isVisible={isAskClaimRefNumberDialogVisible}
          onVisibleChange={visible => this.setState({ isAskClaimRefNumberDialogVisible: visible })}
          onConfirmClick={this.onAskClaimRefNumberConfirm}
        />

        <EdiTextDialog
          width="80%"
          height="wrap_content"
          isVisible={isEdiTextDialogVisible}
          closeDialog={this.closeEdiTextDialog}
          ediText={ediText}
          onVisibleChange={visible => this.setState({ isEdiTextDialogVisible: visible })}
        />
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableEbillingInvoices.propTypes = propTypes;
TableEbillingInvoices.defaultProps = defaultProps;

export default TableEbillingInvoices;
